import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { Detail, StandardTimesKeys } from '../orders/orders-types'

import { productionLogService } from './production-logs-service'
import type {
  ProductionLogEndTimePayload,
  ProductionLogMultiupdateByIdsPayload,
  ProductionLogMultiupdatePayload,
  ProductionLogStartPayload,
  ProductionLogUpdatePayload
} from './production-logs-types'

export const useMarkLogAsDone = ({ onSuccess }: { onSuccess?: (logId: number) => void }) => {
  const queryClient = useQueryClient()

  const updateEndTimeMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ProductionLogEndTimePayload }) => {
      return productionLogService.addEndTime(id, payload)
    },
    onSuccess: (_data, { id }) => {
      toast.success('Marked as done successfully')
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      queryClient.invalidateQueries({ queryKey: ['timeline-orders'] })
      onSuccess?.(id)
    }
  })

  const handleMarkDone = ({ logId, cartId }: { logId: number; cartId: number }) => {
    if (logId) {
      updateEndTimeMutation.mutate({
        id: logId,
        payload: {
          cart_id: cartId,
          time: true
        }
      })
    }
  }

  return { handleMarkDone, isPending: updateEndTimeMutation.isPending }
}

export const useBulkMarkLogsAsDone = ({ onSuccess }: { onSuccess?: () => void } = {}) => {
  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (payload: { ids: number[]; cart_id?: number }) =>
      productionLogService.multiComplete(payload),
    onSuccess: (_data, { ids }) => {
      toast.success(`Marked ${ids.length} item${ids.length === 1 ? '' : 's'} as done`)
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      queryClient.invalidateQueries({ queryKey: ['timeline-orders'] })
      onSuccess?.()
    },
    onError: () => {
      toast.error('Failed to mark items as done')
    }
  })

  return { bulkMarkDone: mutation.mutateAsync, isPending: mutation.isPending }
}

export interface StageMutationPayload {
  stage_id: number
  flow_inven: StandardTimesKeys
  detail: Detail
  production_date?: string
  start_time?: string
}

export const useStageMutation = ({ withInvalidate = true }: { withInvalidate?: boolean } = {}) => {
  const addStartTimeMutation = useMutation({
    mutationFn: ({
      stage_id,
      flow_inven,
      autoid,
      production_date
    }: {
      stage_id: number
      autoid: string
      production_date?: string
      flow_inven: StandardTimesKeys
    }) => productionLogService.addStartTime({ flow_inven, origin_item: autoid, production_date, stage_id }),
    meta: {
      invalidatesQuery: withInvalidate ? ['orders', 'items', 'timeline-orders'] : []
    }
  })

  const updateProductionLogMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ProductionLogUpdatePayload }) =>
      productionLogService.update(id, payload),
    meta: {
      invalidatesQuery: withInvalidate ? ['orders', 'items', 'timeline-orders'] : []
    }
  })

  const handleStageMutation = async ({
    stage_id,
    flow_inven,
    detail,
    production_date,
    start_time
  }: StageMutationPayload) => {
    const currentLog = detail.production_logs.find((log) => log.flow_inven === flow_inven)

    if (currentLog?.start_time || (!!production_date && currentLog && !stage_id)) {
      await updateProductionLogMutation.mutateAsync({
        id: currentLog.id,
        payload: {
          stage_id,
          production_date: production_date || currentLog.production_date,
          start_time: start_time
        }
      })
    } else {
      await addStartTimeMutation.mutateAsync({
        stage_id,
        flow_inven,
        autoid: detail.autoid,
        production_date: production_date || currentLog?.production_date || undefined
      })
    }
  }

  const handleStageReset = (detail: Detail) => {
    const currentLog = detail.production_logs?.at(-1)
    if (currentLog) {
      updateProductionLogMutation.mutate({
        id: currentLog.id,
        payload: { stage_id: null, start_time: null, production_date: null }
      })
    }
  }

  return {
    handleStageMutation,
    handleStageReset,
    isPending: addStartTimeMutation.isPending || updateProductionLogMutation.isPending,
    isError:
      addStartTimeMutation.isError || updateProductionLogMutation.isError || addStartTimeMutation.isError
  }
}

export const useUpdateProductionLog = () => {
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: ProductionLogUpdatePayload }) =>
      productionLogService.update(id, payload),
    meta: {
      successMessage: `Updated successfully`,
      invalidatesQuery: ['orders', 'items', 'timeline-orders']
    }
  })
}

export const useMultiUpdateProductionLog = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: ProductionLogMultiupdatePayload }) =>
      productionLogService.multiupdate(payload),
    meta: {
      successMessage: `Updated successfully`,
      invalidatesQuery: ['orders', 'items', 'timeline-orders']
    }
  })
}

export const useMultiUpdateProductionLogByIds = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: ProductionLogMultiupdateByIdsPayload }) =>
      productionLogService.multiupdateByIds(payload),
    meta: {
      successMessage: `Updated successfully`,
      invalidatesQuery: ['orders', 'items', 'timeline-orders']
    }
  })
}

export const useAddProductionLog = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: ProductionLogStartPayload }) =>
      productionLogService.addStartTime(payload),
    meta: {
      successMessage: `Added successfully`,
      invalidatesQuery: ['orders', 'items', 'timeline-orders']
    }
  })
}
